import { IRegion } from '@/types/explorer';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import { Progress } from '@/components/shadcn/progress';
import {
  formatNumber,
  formatProgressData,
  getMultiplierFactor,
  getRoundPercent,
  getTotalKm,
} from '@/utils/explorer';
import AreaChart from '@/components/ui/charts/AreaChart';
import CustomTooltip from '../charts/CustomTooltip';

interface Props {
  children?: React.ReactNode;
  region?: IRegion;
}

function RegionMetrics(props: Props) {
  const { region } = props;
  const progress = getRoundPercent(region?.overallProgress || 0);
  const totalKm = formatNumber(getTotalKm(region?.totalCoveredL12Cells || 0));
  const uniqueKm = formatNumber(getTotalKm(region?.uniqueCoveredL12Cells || 0));
  const contributors = formatNumber(region?.totalUsers || 0);
  const multiplier = getMultiplierFactor(
    getRoundPercent(region?.multiplierFactor || 0),
  );
  const weight = region?.multiplier;
  const activityProgressData = formatProgressData(
    region?.activityProgressByWeek || {},
    region?.mostRecentDay || '',
  );

  const coverageProgressData = formatProgressData(
    region?.coverageProgressByWeek || {},
    region?.mostRecentDay || '',
  );

  const rawData = region?.resilienceProgressByWeek || {};
  const transformedRawData = Object.keys(rawData).reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: rawData[curr].progress,
    }),
    {},
  );

  const resilienceProgressData = formatProgressData(
    transformedRawData,
    region?.mostRecentDay || '',
  );

  return (
    <div className="flex flex-col gap-2 md:flex-row md:gap-6">
      <div className="flex w-full flex-wrap gap-2 md:w-1/3 md:flex-col md:gap-5">
        <Card className="min-w-24 flex-1 rounded border-none bg-neutral-900 md:max-w-full md:rounded-lg ">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-1 md:text-base">
            <CardTitle>Progress</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 text-sm text-azure-400 md:p-6 md:pt-0 md:text-base">
            <p className="mb-1">{progress}%</p>
            <Progress
              className="h-1.5 bg-neutral-600 [&>*]:bg-azure-400"
              value={progress}
            />
          </CardContent>
        </Card>
        <Card className="min-w-24 flex-1 rounded border-none bg-neutral-900 md:max-w-full md:rounded-lg">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
            <CardTitle>Total Road Km Mapped</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 text-sm text-azure-400 md:p-6 md:pt-0 md:text-xl">
            <p className="leading-normal">{totalKm}</p>
          </CardContent>
        </Card>
        <Card className="min-w-24 flex-1 rounded border-none bg-neutral-900 md:max-w-full md:rounded-lg">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
            <CardTitle>Unique Road Km Mapped</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 text-sm text-azure-400 md:p-6 md:pt-0 md:text-xl">
            <p className="leading-normal">{uniqueKm}</p>
          </CardContent>
        </Card>
        <Card className="min-w-24 flex-1 rounded border-none bg-neutral-900 md:max-w-full md:rounded-lg">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
            <CardTitle>Contributors</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 text-sm text-azure-400 md:p-6 md:pt-0 md:text-xl">
            <p className="leading-normal">{contributors}</p>
          </CardContent>
        </Card>
        <Card className="min-w-24 flex-1 rounded border-none bg-neutral-900 md:max-w-full md:rounded-lg">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
            <CardTitle>Multiplier</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 text-sm text-azure-400 md:p-6 md:pt-0 md:text-xl">
            <p className="leading-normal">{multiplier}</p>
          </CardContent>
        </Card>
        <Card className="min-w-24 flex-1 rounded border-none bg-neutral-900 md:max-w-full md:rounded-lg">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
            <CardTitle>Weight</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 text-sm text-azure-400 md:p-6 md:pt-0 md:text-xl">
            <p className="leading-normal">{weight}</p>
          </CardContent>
        </Card>
      </div>
      <div className="flex w-full flex-col gap-2 md:gap-5">
        <Card className="h-44 w-full rounded border-none bg-neutral-900 md:h-56 md:rounded-lg">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
            <CardTitle>Activity Progress</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 md:p-6 md:pt-0">
            <p className="mb-1 text-xs text-azure-400 md:text-base">
              {getRoundPercent(region?.activityProgress || 0)}%
            </p>
            <div className="h-32 w-full md:h-36">
              <AreaChart
                data={activityProgressData}
                customTooltip={(props: any) => (
                  <CustomTooltip
                    line1={`${Math.round(
                      props?.payload[0]?.value || 0,
                    ).toLocaleString('en-US')}% Activity Progress`}
                    {...props}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>
        <Card className="h-44 w-full rounded border-none bg-neutral-900 md:h-56 md:rounded-lg">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
            <CardTitle>Coverage Progress</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 md:p-6 md:pt-0">
            <p className="mb-1 text-xs text-azure-400 md:text-base">
              {getRoundPercent(region?.coverageProgress || 0)}%
            </p>
            <div className="h-32 w-full md:h-36">
              <AreaChart
                data={coverageProgressData}
                customTooltip={(props: any) => (
                  <CustomTooltip
                    line1={`${Math.round(
                      props?.payload[0]?.value || 0,
                    ).toLocaleString('en-US')}% Coverage Progress`}
                    {...props}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>
        <Card className="h-44 w-full rounded border-none bg-neutral-900 md:h-56 md:rounded-lg">
          <CardHeader className="p-2 pb-1 text-xs md:p-6 md:pb-2 md:text-base">
            <CardTitle>Resilience Progress</CardTitle>
          </CardHeader>
          <CardContent className="p-2 pt-0 md:p-6 md:pt-0">
            <p className="mb-1 text-xs text-azure-400 md:text-base">
              {getRoundPercent(region?.resilienceProgress.progress || 0)}%
            </p>
            <div className="h-32 w-full md:h-36">
              <AreaChart
                data={resilienceProgressData}
                customTooltip={(props: any) => (
                  <CustomTooltip
                    line1={`${Math.round(
                      props?.payload[0]?.value || 0,
                    ).toLocaleString('en-US')}% Resilience Progress`}
                    {...props}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default RegionMetrics;
